import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";
import { injectStatistics, injectMarketing, injectCookieYes } from "@/lib/tracking";
import { t as tr } from "@/lib/tr";

// DSGVO-/CCPA-konformer Cookie-Banner: Alles akzeptieren / Ablehnen / Anpassen.
// Scripts werden AUSSCHLIESSLICH nach Einwilligung geladen. Alternativ: CookieYes-Einbindung.
// Reset-Kanal: Fensterweites Event "yg:consent:reopen" löscht die Einwilligung und öffnet
// den Banner erneut – vom Footer "Cookie-Einstellungen" ausgelöst.
export const CookieConsent = ({ cfg, scope }) => {
  // useTranslation nur, damit der Banner bei einem Sprachwechsel neu rendert –
  // die Texte selbst kommen über tr() aus denselben Namespaces.
  useTranslation(["common"]);
  const key = `yg_consent_${scope}`;
  const [visible, setVisible] = useState(false);
  const [custom, setCustom] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sel, setSel] = useState({ statistics: false, marketing: false });

  const apply = (choices) => {
    if (choices.statistics) injectStatistics(cfg);
    if (choices.marketing) injectMarketing(cfg);
  };

  useEffect(() => {
    if (!cfg?.active) return;
    if (cfg.banner?.mode === "cookieyes" && cfg.cookieyes_code) {
      injectCookieYes(cfg.cookieyes_code);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(key) || "null");
      if (stored) { apply(stored); return; }
    } catch { /* neu fragen */ }
    if (cfg.banner?.enabled !== false) setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  // Reopen-Kanal: Footer-Klick "Cookie-Einstellungen" öffnet den Banner erneut.
  useEffect(() => {
    const reopen = () => {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      setSel({ statistics: false, marketing: false });
      setCustom(true);
      setVisible(true);
    };
    window.addEventListener("yg:consent:reopen", reopen);
    return () => window.removeEventListener("yg:consent:reopen", reopen);
  }, [key]);

  const decide = (choices, action) => {
    const full = { necessary: true, ...choices };
    try { localStorage.setItem(key, JSON.stringify(full)); } catch { /* ignore */ }
    // Rechtssicherer Consent-Nachweis (Art. 7 DSGVO): Zeitstempel, IP, Quelle, Page-URL.
    api.post("/consent/record", {
      scope,
      necessary: true,
      statistics: !!full.statistics,
      marketing: !!full.marketing,
      source: action || "banner",
      page_url: typeof window !== "undefined" ? window.location.href : null,
      consent_version: "2026-02",
    }).catch(() => {});
    apply(full);
    setVisible(false);
  };

  // Solange ein modales Fenster offen ist (Start-Popup am Tisch, Produktdialog, Checkout …),
  // wird der Banner ausgeblendet – er überdeckte sonst die Schaltflächen des Dialogs.
  // Nach dem Schließen erscheint er automatisch wieder.
  useEffect(() => {
    const check = () => setModalOpen(!!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ["data-state"] });
    return () => mo.disconnect();
  }, []);

  if (!visible || modalOpen) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[80] p-3 sm:p-4" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto bg-white border border-stone-200 rounded-2xl shadow-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-bold text-sm text-stone-900">{cfg.banner?.title || tr("common:cookie.title", { defaultValue: "Wir respektieren Ihre Privatsphäre" })}</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">
              {cfg.banner?.text || tr("common:cookie.text", { defaultValue: "Wir verwenden Cookies und ähnliche Technologien, um unsere Website zu betreiben und mit deiner Einwilligung Statistiken und Marketing zu verbessern. Du kannst deine Auswahl jederzeit anpassen. (DSGVO / CCPA: Deine Ablehnung wird respektiert und es werden keine Daten verkauft.)" })}
              {" "}
              <a href={cfg.banner?.privacy_url || "/datenschutz"} target={cfg.banner?.privacy_url ? "_blank" : undefined} rel="noreferrer" className="underline" data-testid="cookie-privacy-link">
                {tr("common:cookie.privacy", { defaultValue: "Datenschutzerklärung" })}
              </a>
            </p>
          </div>
        </div>
        {custom && (
          <div className="mt-3 grid sm:grid-cols-3 gap-3 border-t border-stone-100 pt-3" data-testid="cookie-custom-panel">
            <label className="flex items-center gap-2 text-xs text-stone-500"><Switch checked disabled /> {tr("common:cookie.necessary", { defaultValue: "Notwendig (immer aktiv)" })}</label>
            <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
              <Switch checked={sel.statistics} onCheckedChange={(v) => setSel({ ...sel, statistics: v })} data-testid="cookie-statistics-toggle" /> {tr("common:cookie.statistics", { defaultValue: "Statistik (Google Tag Manager)" })}
            </label>
            <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
              <Switch checked={sel.marketing} onCheckedChange={(v) => setSel({ ...sel, marketing: v })} data-testid="cookie-marketing-toggle" /> {tr("common:cookie.marketing", { defaultValue: "Marketing (Meta, TikTok, Pinterest)" })}
            </label>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" className="bg-stone-900 hover:bg-stone-700" onClick={() => decide({ statistics: true, marketing: true }, "accept_all")} data-testid="cookie-accept-all">
            {tr("common:cookie.acceptAll", { defaultValue: "Alle akzeptieren" })}
          </Button>
          <Button size="sm" variant="outline" onClick={() => decide({ statistics: false, marketing: false }, "reject")} data-testid="cookie-reject">
            {tr("common:cookie.reject", { defaultValue: "Ablehnen" })}
          </Button>
          {custom
            ? <Button size="sm" variant="outline" onClick={() => decide(sel, "custom")} data-testid="cookie-save-selection">{tr("common:cookie.saveSelection", { defaultValue: "Auswahl speichern" })}</Button>
            : <Button size="sm" variant="ghost" className="text-stone-500" onClick={() => setCustom(true)} data-testid="cookie-customize">{tr("common:cookie.customize", { defaultValue: "Anpassen" })}</Button>}
        </div>
      </div>
    </div>
  );
};
